import { useCallback, useRef, useState } from 'react';

import { NotificationSeverity } from '@/components/notifications/Notification';
import { useNotification } from '@/hooks';
import { getErrorMessage } from '@/utils';

import type {
  InviteUserAccessOnlySchema,
  UserScopeExclusionRecord,
  UserScopeRecord,
} from '../models';
import {
  useCreateExclusionMutation,
  useCreateScopeMutation,
  useDeleteUserScopeExclusionMutation,
  useDeleteUserScopeMutation,
  useLazyGetUserScopeExclusionsPageQuery,
  useLazyGetUserScopesPageQuery,
} from '../services';
import { loadAllUserScopeExclusions, loadAllUserScopes } from '../utils/fetchUserScopeLists';
import { buildScopesAndExclusionsFromAccess } from '../utils/inviteUserScopes';

export type ReplaceUserAccessScopesCache = {
  existingScopes: UserScopeRecord[];
  existingExclusions: UserScopeExclusionRecord[];
};

/**
 * Sincroniza el alcance del usuario con el formulario (misma lógica que invitación):
 * borra todas las inclusiones/exclusiones existentes y vuelve a crear según el estado del formulario.
 */
const useReplaceUserAccessScopes = () => {
  const { showNotification } = useNotification();
  const [fetchScopesPage] = useLazyGetUserScopesPageQuery();
  const [fetchExclusionsPage] = useLazyGetUserScopeExclusionsPageQuery();
  const fetchScopesRef = useRef(fetchScopesPage);
  const fetchExclusionsRef = useRef(fetchExclusionsPage);
  fetchScopesRef.current = fetchScopesPage;
  fetchExclusionsRef.current = fetchExclusionsPage;

  const [createScope] = useCreateScopeMutation();
  const [createExclusion] = useCreateExclusionMutation();
  const [deleteScope] = useDeleteUserScopeMutation();
  const [deleteExclusion] = useDeleteUserScopeExclusionMutation();

  const [isReplacingAccess, setIsReplacingAccess] = useState(false);

  const replaceUserAccessScopes = useCallback(
    async (
      userId: string,
      access: InviteUserAccessOnlySchema,
      cache?: ReplaceUserAccessScopesCache
    ) => {
      setIsReplacingAccess(true);
      try {
        const existingScopes =
          cache?.existingScopes ??
          (await loadAllUserScopes(
            (args) => fetchScopesRef.current(args),
            userId
          ));
        const existingExclusions =
          cache?.existingExclusions ??
          (await loadAllUserScopeExclusions(
            (args) => fetchExclusionsRef.current(args),
            userId
          ));

        for (const row of existingExclusions) {
          await deleteExclusion({
            userId,
            exclusionRowId: row.id,
          }).unwrap();
        }
        for (const row of existingScopes) {
          await deleteScope({ userId, scopeRowId: row.id }).unwrap();
        }

        const { scopes, exclusions } =
          buildScopesAndExclusionsFromAccess(access);

        for (const scope of scopes) {
          await createScope({ userId, scope }).unwrap();
        }
        for (const exclusion of exclusions) {
          await createExclusion({ userId, exclusion }).unwrap();
        }

        showNotification(
          'Alcance del usuario actualizado correctamente',
          NotificationSeverity.success
        );
        return true;
      } catch (error: unknown) {
        showNotification(
          getErrorMessage(
            error,
            'No se pudo actualizar el alcance del usuario.'
          ),
          NotificationSeverity.error
        );
        return false;
      } finally {
        setIsReplacingAccess(false);
      }
    },
    [
      createScope,
      createExclusion,
      deleteScope,
      deleteExclusion,
      showNotification,
    ]
  );

  return { replaceUserAccessScopes, isReplacingAccess };
};

export default useReplaceUserAccessScopes;
